import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { DashboardProject } from '../types';

interface ProjectState {
  project: DashboardProject | null;
  isLoading: boolean;
  error: string | null;
}

type ProjectAction =
  | { type: 'SET_PROJECT'; payload: DashboardProject }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

interface ProjectContextType extends ProjectState {
  setProject: (project: DashboardProject) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const initialState: ProjectState = {
  project: null,
  isLoading: false,
  error: null,
};

function projectReducer(state: ProjectState, action: ProjectAction): ProjectState {
  switch (action.type) {
    case 'SET_PROJECT':
      return { ...state, project: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    default:
      return state;
  }
}

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(projectReducer, initialState);

  const setProject = (project: DashboardProject) => {
    dispatch({ type: 'SET_PROJECT', payload: project });
  };

  const setLoading = (isLoading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: isLoading });
  };

  const setError = (error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  return (
    <ProjectContext.Provider
      value={{
        ...state,
        setProject,
        setLoading,
        setError,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
} 